/**
 * Location Intelligence Service for HoneyDo Platform
 * Provides smart location detection, geocoding, and location-aware task management
 * Integrates with weather services and travel time calculations
 */

import { weatherSchedulingService } from './WeatherSchedulingService';
import { apiClient } from '../api/client';
import { googleMapsLoader, type GoogleMapsLoadResult } from './GoogleMapsLoader';

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export interface LocationDetails {
  id: string;
  name: string;
  address: string;
  coordinates: LocationCoordinates;
  type: LocationType;
  category?: LocationCategory;
  isVerified: boolean;
  confidence: number;
  metadata?: LocationMetadata;
}

export interface LocationMetadata {
  placeId?: string;
  phoneNumber?: string;
  website?: string;
  businessHours?: BusinessHours;
  rating?: number;
  priceLevel?: number;
  travelTimeFromHome?: number; // in minutes
}

export interface BusinessHours {
  [key: string]: { open: string; close: string; closed?: boolean };
}

export type LocationType = 'home' | 'work' | 'store' | 'service' | 'outdoor' | 'venue' | 'address' | 'custom';

export type LocationCategory = 
  | 'home_improvement' // Home Depot, Lowes, etc.
  | 'grocery' // Grocery stores, markets
  | 'retail' // General retail stores
  | 'services' // Contractors, service providers
  | 'outdoor' // Parks, beaches, trails
  | 'healthcare' // Medical facilities
  | 'automotive' // Car services, gas stations
  | 'personal' // Hair salon, gym, etc.
  | 'dining' // Restaurants, cafes
  | 'entertainment' // Movies, events
  | 'government' // DMV, city hall
  | 'other';

export interface LocationSuggestion {
  location: LocationDetails;
  reason: string;
  confidence: number;
  alternatives: LocationDetails[];
}

export interface TaskLocationInference {
  taskTitle: string;
  suggestedLocation?: LocationDetails;
  locationRequired: boolean;
  confidence: number;
  inferenceReason: string;
  needsUserConfirmation: boolean;
  alternatives: LocationDetails[];
}

export interface WeatherLocationData {
  location: LocationDetails;
  currentWeather: any;
  forecast: any[];
  alerts: WeatherAlert[];
  suitabilityScore: number; // 0-1 for outdoor tasks
}

export interface WeatherAlert {
  type: 'warning' | 'watch' | 'advisory';
  severity: 'minor' | 'moderate' | 'severe' | 'extreme';
  title: string;
  description: string;
  startTime: string;
  endTime: string;
}

export interface TravelTimeResult {
  origin: LocationDetails;
  destination: LocationDetails;
  durationMinutes: number;
  distanceMiles: number;
  mode: 'driving' | 'walking' | 'transit';
  route?: any;
  trafficImpact?: number; // Multiplier for current traffic
}

interface LocationPreferences {
  homeLocation?: LocationDetails;
  workLocation?: LocationDetails;
  favoriteLocations: LocationDetails[];
  recentLocations: LocationDetails[];
  locationCategories: LocationCategory[];
  autoDetectHome: boolean;
  requestLocationPermission: boolean;
  privacyLevel: 'precise' | 'approximate' | 'city_only';
  maxTravelDistance: number; // miles for suggestions
}

class LocationIntelligenceService {
  private currentPosition: LocationCoordinates | null = null;
  private positionWatchId: number | null = null;
  private hasGoogleMaps: boolean = false;
  private geocoder: any = null; // Google Maps Geocoder
  private placesService: any = null; // Google Places Service
  private locationCache: Map<string, { data: LocationDetails; timestamp: number }> = new Map();
  private travelTimeCache: Map<string, { data: TravelTimeResult; timestamp: number }> = new Map();
  private weatherCache: Map<string, { data: WeatherLocationData; timestamp: number }> = new Map();
  private preferences: LocationPreferences;
  private cacheExpiryMinutes = 30;

  // Task title patterns for location inference
  private readonly locationPatterns = {
    homeKeywords: [
      'clean', 'organize', 'vacuum', 'dust', 'wash dishes', 'laundry', 'bedroom', 'kitchen', 
      'bathroom', 'living room', 'basement', 'attic', 'garage', 'lawn', 'yard', 'garden', 
      'roof', 'driveway', 'porch', 'deck', 'fence', 'fix', 'repair', 'install', 'replace'
    ],
    
    storePatterns: {
      'home_improvement': [
        'home depot', 'lowes', 'hardware store', 'lumber', 'supplies', 'tools', 'paint', 
        'plumbing supplies', 'electrical supplies', 'garden center', 'build', 'repair materials'
      ],
      'grocery': [
        'grocery store', 'supermarket', 'safeway', 'kroger', 'whole foods', 'trader joes',
        'costco', 'sams club', 'walmart', 'target', 'food shopping', 'groceries'
      ],
      'automotive': [
        'gas station', 'oil change', 'car wash', 'auto repair', 'tire shop', 'mechanic',
        'dmv', 'registration', 'inspection'
      ],
      'healthcare': [
        'doctor', 'dentist', 'hospital', 'clinic', 'pharmacy', 'cvs', 'walgreens', 
        'rite aid', 'medical', 'appointment'
      ]
    },

    outdoorKeywords: [
      'beach', 'park', 'trail', 'hiking', 'camping', 'outdoor', 'outside', 'nature',
      'lake', 'river', 'mountain', 'playground', 'sports field', 'tennis court', 'golf'
    ],

    travelKeywords: [
      'go to', 'visit', 'drive to', 'pick up', 'drop off', 'meet at', 'appointment at',
      'shopping at', 'stop by', 'swing by'
    ]
  };

  private defaultPreferences: LocationPreferences = {
    homeLocation: undefined,
    workLocation: undefined,
    favoriteLocations: [],
    recentLocations: [],
    locationCategories: ['home_improvement', 'grocery', 'healthcare', 'automotive'],
    autoDetectHome: true,
    requestLocationPermission: true,
    privacyLevel: 'precise',
    maxTravelDistance: 25 // miles
  };

  constructor() {
    this.preferences = { ...this.defaultPreferences };
    this.init();
  }

  /**
   * Initialize the location intelligence service
   */
  async init(): Promise<void> {
    console.log('Initializing LocationIntelligenceService...');

    try {
      // Load user preferences
      await this.loadPreferences();

      // Initialize geolocation if permitted
      if (this.preferences.requestLocationPermission) {
        await this.initializeGeolocation();
      }

      // Initialize Google Maps services if available
      await this.initializeGoogleMaps();

      console.log('LocationIntelligenceService initialized successfully');
    } catch (error) {
      console.error('Failed to initialize LocationIntelligenceService:', error);
    }
  }

  /**
   * Initialize geolocation services
   */
  private async initializeGeolocation(): Promise<void> {
    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: this.preferences.privacyLevel === 'precise',
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        });
      });

      this.currentPosition = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };

      // Auto-detect home location if enabled and not set
      if (this.preferences.autoDetectHome && !this.preferences.homeLocation) {
        const homeLocation = await this.detectHomeLocation(this.currentPosition);
        if (homeLocation) {
          this.preferences.homeLocation = homeLocation;
          await this.savePreferences();
        }
      }

      console.log('Geolocation initialized');
    } catch (error) {
      console.error('Geolocation initialization failed:', error);
    }
  }

  /**
   * Initialize Google Maps services for geocoding and places
   * Using Seawater service integration patterns
   */
  private async initializeGoogleMaps(): Promise<void> {
    try {
      const loadResult: GoogleMapsLoadResult = await googleMapsLoader.loadGoogleMaps({
        libraries: ['places', 'geometry'],
        region: 'US'
      });

      if (!loadResult.success) {
        console.warn('Google Maps API not available:', loadResult.error);
        this.hasGoogleMaps = false;
        return;
      }

      const googleMaps = loadResult.maps!;
      this.geocoder = new googleMaps.Geocoder();
      
      // Create a dummy map for Places service
      const mapDiv = document.createElement('div');
      const map = new googleMaps.Map(mapDiv);
      this.placesService = new googleMaps.places.PlacesService(map);
      
      this.hasGoogleMaps = true;
      console.log('Google Maps services initialized');
    } catch (error) {
      console.warn('Google Maps not available, using fallback geocoding:', error);
      this.hasGoogleMaps = false;
    }
  }

  /**
   * Detect home location from current position
   */
  private async detectHomeLocation(position: LocationCoordinates): Promise<LocationDetails | null> {
    try {
      const address = await this.reverseGeocode(position);
      if (address) {
        return {
          id: 'home_detected',
          name: 'Home',
          address: address.formatted_address || address.address,
          coordinates: position,
          type: 'home',
          isVerified: false,
          confidence: 0.8,
          metadata: {
            travelTimeFromHome: 0
          }
        };
      }
    } catch (error) {
      console.warn('Failed to detect home location:', error);
    }
    return null;
  }

  /**
   * Infer location requirements from task title and description
   */
  async inferTaskLocation(taskTitle: string, description?: string, category?: string): Promise<TaskLocationInference> {
    const lowerTitle = taskTitle.toLowerCase();
    const lowerDesc = description?.toLowerCase() || '';
    const fullText = `${lowerTitle} ${lowerDesc}`;

    // Check if task explicitly mentions travel
    const needsTravel = this.locationPatterns.travelKeywords.some(keyword => 
      fullText.includes(keyword)
    );

    // Check if task is home-based
    const isHomeBased = this.locationPatterns.homeKeywords.some(keyword => 
      fullText.includes(keyword)
    );

    // Check for specific store mentions
    let suggestedStores: LocationDetails[] = [];
    for (const [storeCategory, patterns] of Object.entries(this.locationPatterns.storePatterns)) {
      const matches = patterns.some(pattern => fullText.includes(pattern));
      if (matches) {
        suggestedStores = await this.findNearbyPlaces(storeCategory as LocationCategory, 5);
        break;
      }
    }

    // Check for outdoor locations
    const isOutdoor = this.locationPatterns.outdoorKeywords.some(keyword => 
      fullText.includes(keyword)
    );

    // Determine suggested location
    let suggestedLocation: LocationDetails | undefined;
    let locationRequired = false;
    let confidence = 0.5;
    let inferenceReason = 'Task appears to be location-independent';
    let needsUserConfirmation = false;
    let alternatives: LocationDetails[] = [];

    if (isHomeBased && this.preferences.homeLocation) {
      suggestedLocation = this.preferences.homeLocation;
      confidence = 0.9;
      inferenceReason = 'Task appears to be home-based';
      locationRequired = false;
    } else if (suggestedStores.length > 0) {
      suggestedLocation = suggestedStores[0];
      alternatives = suggestedStores.slice(1);
      confidence = 0.85;
      inferenceReason = `Task mentions supplies/services available at ${suggestedLocation.name}`;
      locationRequired = true;
      needsUserConfirmation = true;
    } else if (needsTravel) {
      locationRequired = true;
      confidence = 0.7;
      inferenceReason = 'Task mentions going somewhere specific';
      needsUserConfirmation = true;
      
      // Try to extract specific location from text
      const extractedLocation = await this.extractLocationFromText(fullText);
      if (extractedLocation) {
        suggestedLocation = extractedLocation;
        confidence = 0.8;
        inferenceReason = 'Location extracted from task description';
      }
    } else if (isOutdoor) {
      locationRequired = true;
      confidence = 0.75;
      inferenceReason = 'Task appears to be outdoors - location needed for weather';
      needsUserConfirmation = true;
      
      // Suggest nearby parks or outdoor venues
      const outdoorLocations = await this.findNearbyPlaces('outdoor', 3);
      alternatives = outdoorLocations;
    }

    return {
      taskTitle,
      suggestedLocation,
      locationRequired,
      confidence,
      inferenceReason,
      needsUserConfirmation,
      alternatives
    };
  }

  /**
   * Extract specific location mentions from text
   */
  private async extractLocationFromText(text: string): Promise<LocationDetails | null> {
    // Simple pattern matching for common location formats
    const locationPatterns = [
      /at (.+?)(?:\s|$)/i,
      /to (.+?)(?:\s|$)/i,
      /from (.+?)(?:\s|$)/i,
      /near (.+?)(?:\s|$)/i
    ];

    for (const pattern of locationPatterns) {
      const match = text.match(pattern);
      if (match && match[1].length > 2) {
        const locationQuery = match[1].trim();
        const geocoded = await this.geocodeLocation(locationQuery);
        if (geocoded) {
          return geocoded;
        }
      }
    }

    return null;
  }

  /**
   * Geocode a location query into coordinates and details
   */
  async geocodeLocation(query: string): Promise<LocationDetails | null> {
    const cacheKey = `geocode_${query.toLowerCase()}`;
    const cached = this.locationCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiryMinutes * 60 * 1000) {
      return cached.data;
    }

    try {
      let result: LocationDetails | null = null;

      // Try Google Maps Geocoder first
      if (this.geocoder) {
        result = await this.googleGeocode(query);
      }

      // Fallback to browser geolocation API or other services
      if (!result) {
        result = await this.fallbackGeocode(query);
      }

      if (result) {
        this.locationCache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
      }

      return result;
    } catch (error) {
      console.error('Geocoding failed:', error);
      return null;
    }
  }

  /**
   * Google Maps geocoding
   */
  private async googleGeocode(query: string): Promise<LocationDetails | null> {
    return new Promise((resolve) => {
      this.geocoder.geocode({ address: query }, (results: any[], status: string) => {
        if (status === 'OK' && results.length > 0) {
          const result = results[0];
          const location: LocationDetails = {
            id: `geocoded_${Date.now()}`,
            name: result.address_components?.[0]?.long_name || query,
            address: result.formatted_address,
            coordinates: {
              latitude: result.geometry.location.lat(),
              longitude: result.geometry.location.lng(),
              accuracy: 10 // Assume good accuracy for geocoded results
            },
            type: this.inferLocationType(result),
            isVerified: true,
            confidence: 0.9,
            metadata: {
              placeId: result.place_id
            }
          };

          resolve(location);
        } else {
          resolve(null);
        }
      });
    });
  }

  /**
   * Fallback geocoding using a free service
   */
  private async fallbackGeocode(query: string): Promise<LocationDetails | null> {
    try {
      // Using Nominatim (OpenStreetMap) as fallback
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
      const response = await fetch(url);
      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        return {
          id: `nominatim_${Date.now()}`,
          name: result.display_name.split(',')[0],
          address: result.display_name,
          coordinates: {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
            accuracy: 50 // Lower accuracy for free service
          },
          type: this.inferLocationTypeFromOSM(result.type),
          isVerified: false,
          confidence: 0.7
        };
      }
    } catch (error) {
      console.warn('Fallback geocoding failed:', error);
    }

    return null;
  }

  /**
   * Reverse geocode coordinates to address
   */
  async reverseGeocode(coordinates: LocationCoordinates): Promise<any> {
    try {
      if (this.geocoder) {
        return new Promise((resolve, reject) => {
          this.geocoder.geocode(
            { location: { lat: coordinates.latitude, lng: coordinates.longitude } },
            (results: any[], status: string) => {
              if (status === 'OK' && results.length > 0) {
                resolve(results[0]);
              } else {
                reject(new Error(`Reverse geocoding failed: ${status}`));
              }
            }
          );
        });
      } else {
        // Fallback to Nominatim
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coordinates.latitude}&lon=${coordinates.longitude}`;
        const response = await fetch(url);
        return await response.json();
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      throw error;
    }
  }

  /**
   * Find nearby places by category
   */
  async findNearbyPlaces(category: LocationCategory, limit: number = 10): Promise<LocationDetails[]> {
    if (!this.currentPosition) {
      console.warn('Current position not available for nearby search');
      return [];
    }

    const cacheKey = `nearby_${category}_${this.currentPosition.latitude}_${this.currentPosition.longitude}`;
    const cached = this.locationCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiryMinutes * 60 * 1000) {
      return [cached.data].flat().slice(0, limit);
    }

    try {
      let places: LocationDetails[] = [];

      // Try Google Places API first
      if (this.placesService) {
        places = await this.googlePlacesSearch(category, limit);
      }

      // Fallback to other sources if needed
      if (places.length === 0) {
        places = await this.fallbackPlacesSearch(category, limit);
      }

      // Cache results
      if (places.length > 0) {
        this.locationCache.set(cacheKey, {
          data: places as any,
          timestamp: Date.now()
        });
      }

      return places;
    } catch (error) {
      console.error('Places search failed:', error);
      return [];
    }
  }

  /**
   * Google Places search
   */
  private async googlePlacesSearch(category: LocationCategory, limit: number): Promise<LocationDetails[]> {
    return new Promise((resolve) => {
      if (!this.currentPosition || !this.placesService) {
        resolve([]);
        return;
      }

      const searchTypes = this.getGooglePlaceTypes(category);
      const request = {
        location: new (window as any).google.maps.LatLng(this.currentPosition.latitude, this.currentPosition.longitude),
        radius: this.preferences.maxTravelDistance * 1609.34, // Convert miles to meters
        types: searchTypes
      };

      this.placesService.nearbySearch(request, (results: any[], status: string) => {
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && results) {
          const places = results.slice(0, limit).map((place: any): LocationDetails => ({
            id: place.place_id,
            name: place.name,
            address: place.vicinity || place.formatted_address,
            coordinates: {
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              accuracy: 10
            },
            type: 'store',
            category,
            isVerified: true,
            confidence: 0.9,
            metadata: {
              placeId: place.place_id,
              rating: place.rating,
              priceLevel: place.price_level,
              travelTimeFromHome: undefined // Will be calculated if needed
            }
          }));

          resolve(places);
        } else {
          resolve([]);
        }
      });
    });
  }

  /**
   * Fallback places search using alternative APIs
   */
  private async fallbackPlacesSearch(category: LocationCategory, limit: number): Promise<LocationDetails[]> {
    // This would integrate with alternative APIs like Foursquare, Yelp, etc.
    // For now, return empty array
    console.warn('Fallback places search not implemented yet');
    return [];
  }

  /**
   * Calculate travel time between locations
   */
  async calculateTravelTime(
    from: LocationDetails, 
    to: LocationDetails, 
    mode: 'driving' | 'walking' | 'transit' = 'driving'
  ): Promise<TravelTimeResult | null> {
    const cacheKey = `travel_${from.id}_${to.id}_${mode}`;
    const cached = this.travelTimeCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiryMinutes * 60 * 1000) {
      return cached.data;
    }

    try {
      // Calculate straight-line distance first
      const distance = this.calculateHaversineDistance(from.coordinates, to.coordinates);
      
      // For now, use simple estimation (will be enhanced with real APIs)
      let estimatedDuration: number;
      switch (mode) {
        case 'walking':
          estimatedDuration = distance * 20; // ~3 mph walking speed
          break;
        case 'transit':
          estimatedDuration = distance * 4; // Transit including wait times
          break;
        case 'driving':
        default:
          estimatedDuration = distance * 2.5; // ~25 mph average with stops
          break;
      }

      const result: TravelTimeResult = {
        origin: from,
        destination: to,
        durationMinutes: Math.round(estimatedDuration),
        distanceMiles: distance,
        mode,
        trafficImpact: 1.0
      };

      // Cache result
      this.travelTimeCache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });

      return result;
    } catch (error) {
      console.error('Travel time calculation failed:', error);
      return null;
    }
  }

  /**
   * Get weather data for multiple locations
   */
  async getMultiLocationWeather(locations: LocationDetails[]): Promise<WeatherLocationData[]> {
    const results: WeatherLocationData[] = [];

    for (const location of locations) {
      const cacheKey = `weather_${location.id}`;
      const cached = this.weatherCache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < this.cacheExpiryMinutes * 60 * 1000) {
        results.push(cached.data);
        continue;
      }

      try {
        // Get weather data using the existing weather service
        const forecast = await weatherSchedulingService.getWeatherForecast(
          undefined,
          location.coordinates.latitude,
          location.coordinates.longitude,
          7
        );

        const currentWeather = forecast[0];
        const alerts = this.extractWeatherAlerts(forecast);
        const suitabilityScore = this.calculateOutdoorSuitability(currentWeather);

        const weatherData: WeatherLocationData = {
          location,
          currentWeather,
          forecast,
          alerts,
          suitabilityScore
        };

        this.weatherCache.set(cacheKey, {
          data: weatherData,
          timestamp: Date.now()
        });

        results.push(weatherData);
      } catch (error) {
        console.warn(`Failed to get weather for ${location.name}:`, error);
      }
    }

    return results;
  }

  /**
   * Get location suggestions for a task
   */
  async getLocationSuggestions(taskTitle: string, description?: string, category?: string): Promise<LocationSuggestion[]> {
    const inference = await this.inferTaskLocation(taskTitle, description, category);
    const suggestions: LocationSuggestion[] = [];

    // Add inferred location as primary suggestion
    if (inference.suggestedLocation) {
      suggestions.push({
        location: inference.suggestedLocation,
        reason: inference.inferenceReason,
        confidence: inference.confidence,
        alternatives: inference.alternatives
      });
    }

    // Add recent locations if relevant
    const recentRelevant = this.preferences.recentLocations
      .filter(loc => this.isLocationRelevant(loc, taskTitle, category))
      .slice(0, 3);

    for (const location of recentRelevant) {
      suggestions.push({
        location,
        reason: 'Recently used location',
        confidence: 0.6,
        alternatives: []
      });
    }

    // Add favorites if relevant
    const favoriteRelevant = this.preferences.favoriteLocations
      .filter(loc => this.isLocationRelevant(loc, taskTitle, category))
      .slice(0, 2);

    for (const location of favoriteRelevant) {
      suggestions.push({
        location,
        reason: 'Favorite location',
        confidence: 0.7,
        alternatives: []
      });
    }

    return suggestions.slice(0, 5); // Limit to top 5 suggestions
  }

  /**
   * Add location to favorites
   */
  async addToFavorites(location: LocationDetails): Promise<void> {
    const exists = this.preferences.favoriteLocations.some(fav => fav.id === location.id);
    if (!exists) {
      this.preferences.favoriteLocations.push(location);
      await this.savePreferences();
    }
  }

  /**
   * Add location to recent locations
   */
  async addToRecent(location: LocationDetails): Promise<void> {
    // Remove if already exists
    this.preferences.recentLocations = this.preferences.recentLocations.filter(
      recent => recent.id !== location.id
    );
    
    // Add to front
    this.preferences.recentLocations.unshift(location);
    
    // Keep only last 10
    this.preferences.recentLocations = this.preferences.recentLocations.slice(0, 10);
    
    await this.savePreferences();
  }

  /**
   * Set home location
   */
  async setHomeLocation(location: LocationDetails): Promise<void> {
    this.preferences.homeLocation = { ...location, type: 'home' };
    await this.savePreferences();
  }

  /**
   * Set work location
   */
  async setWorkLocation(location: LocationDetails): Promise<void> {
    this.preferences.workLocation = { ...location, type: 'work' };
    await this.savePreferences();
  }

  // Helper methods

  private calculateHaversineDistance(coord1: LocationCoordinates, coord2: LocationCoordinates): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(coord2.latitude - coord1.latitude);
    const dLon = this.toRadians(coord2.longitude - coord1.longitude);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.toRadians(coord1.latitude)) * Math.cos(this.toRadians(coord2.latitude)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  private inferLocationType(googleResult: any): LocationType {
    const types = googleResult.types || [];
    
    if (types.includes('home') || types.includes('street_address')) return 'home';
    if (types.includes('establishment') || types.includes('store')) return 'store';
    if (types.includes('park') || types.includes('natural_feature')) return 'outdoor';
    if (types.includes('hospital') || types.includes('doctor')) return 'service';
    
    return 'address';
  }

  private inferLocationTypeFromOSM(osmType: string): LocationType {
    const typeMapping: Record<string, LocationType> = {
      'house': 'home',
      'residential': 'home',
      'shop': 'store',
      'amenity': 'service',
      'leisure': 'outdoor',
      'natural': 'outdoor'
    };
    
    return typeMapping[osmType] || 'address';
  }

  private getGooglePlaceTypes(category: LocationCategory): string[] {
    const typeMapping: Record<LocationCategory, string[]> = {
      'home_improvement': ['hardware_store', 'home_goods_store', 'general_contractor'],
      'grocery': ['grocery_or_supermarket', 'supermarket'],
      'retail': ['shopping_mall', 'store'],
      'services': ['general_contractor', 'plumber', 'electrician'],
      'outdoor': ['park', 'campground', 'tourist_attraction'],
      'healthcare': ['hospital', 'doctor', 'pharmacy'],
      'automotive': ['gas_station', 'car_repair', 'car_wash'],
      'personal': ['beauty_salon', 'gym', 'spa'],
      'dining': ['restaurant', 'meal_takeaway', 'cafe'],
      'entertainment': ['movie_theater', 'amusement_park', 'museum'],
      'government': ['local_government_office', 'post_office'],
      'other': ['establishment']
    };

    return typeMapping[category] || ['establishment'];
  }

  private isLocationRelevant(location: LocationDetails, taskTitle: string, category?: string): boolean {
    const lowerTitle = taskTitle.toLowerCase();
    
    // Check if location category matches task context
    if (category && location.category) {
      const relevantCategories: Record<string, LocationCategory[]> = {
        'plumbing': ['home_improvement', 'services'],
        'electrical': ['home_improvement', 'services'],
        'landscaping': ['home_improvement', 'outdoor'],
        'general': ['home_improvement', 'grocery', 'retail']
      };
      
      return relevantCategories[category]?.includes(location.category) || false;
    }

    // Check for keyword matches in location name
    const locationWords = location.name.toLowerCase().split(' ');
    const titleWords = lowerTitle.split(' ');
    
    return locationWords.some(word => titleWords.includes(word));
  }

  private extractWeatherAlerts(forecast: any[]): WeatherAlert[] {
    // Extract weather warnings from forecast data
    const alerts: WeatherAlert[] = [];
    
    for (const day of forecast) {
      if (day.conditions === 'rainy' && day.rainChance > 70) {
        alerts.push({
          type: 'warning',
          severity: 'moderate',
          title: 'Heavy Rain Expected',
          description: `${day.rainChance}% chance of rain on ${day.date}`,
          startTime: day.date,
          endTime: day.date
        });
      }
      
      if (day.tempHigh > 95) {
        alerts.push({
          type: 'advisory',
          severity: 'minor',
          title: 'High Temperature',
          description: `Temperature expected to reach ${day.tempHigh}°F`,
          startTime: day.date,
          endTime: day.date
        });
      }
    }
    
    return alerts;
  }

  private calculateOutdoorSuitability(weather: any): number {
    let score = 1.0;
    
    // Temperature penalties
    if (weather.tempHigh > 90) score -= 0.3;
    if (weather.tempLow < 40) score -= 0.2;
    
    // Weather condition penalties
    if (weather.conditions === 'rainy') score -= 0.5;
    if (weather.conditions === 'snow') score -= 0.4;
    if (weather.rainChance > 50) score -= 0.3;
    
    return Math.max(0, Math.min(1, score));
  }

  private async loadPreferences(): Promise<void> {
    try {
      const stored = localStorage.getItem('honeydo_location_preferences');
      if (stored) {
        this.preferences = { ...this.defaultPreferences, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Failed to load location preferences:', error);
    }
  }

  private async savePreferences(): Promise<void> {
    try {
      localStorage.setItem('honeydo_location_preferences', JSON.stringify(this.preferences));
    } catch (error) {
      console.error('Failed to save location preferences:', error);
    }
  }

  // Public API

  getCurrentLocation(): LocationCoordinates | null {
    return this.currentPosition;
  }

  getHomeLocation(): LocationDetails | undefined {
    return this.preferences.homeLocation;
  }

  getWorkLocation(): LocationDetails | undefined {
    return this.preferences.workLocation;
  }

  getFavoriteLocations(): LocationDetails[] {
    return this.preferences.favoriteLocations;
  }

  getRecentLocations(): LocationDetails[] {
    return this.preferences.recentLocations;
  }

  async updatePreferences(updates: Partial<LocationPreferences>): Promise<void> {
    this.preferences = { ...this.preferences, ...updates };
    await this.savePreferences();
  }

  getPreferences(): LocationPreferences {
    return { ...this.preferences };
  }

  cleanup(): void {
    if (this.positionWatchId) {
      navigator.geolocation.clearWatch(this.positionWatchId);
      this.positionWatchId = null;
    }
  }
}

// Export singleton instance
export const locationIntelligenceService = new LocationIntelligenceService();

export default locationIntelligenceService;